/**
 * Accounting module types
 */

import type { LandedCostAllocationMethod } from '../../../generated/config-enums';
import type { TagSummary } from '../../../shared/components/tags';
import type { PaymentTermsBasis } from '../../../shared/payment-terms';
import { JournalEntryType } from '../../../generated/api/models/journalEntryType';
// Reimbursement-payer value sets — re-exported (with the rationale) in the
// REIMBURSEMENT PAYERS section below; imported here because the BankAccount*
// shapes reference them locally.
import { CardOwnership } from '../../../generated/api/models/cardOwnership';
import { PayerEntityTable } from '../../../generated/api/models/payerEntityTable';
// ProductReference config snapshot shape — schema-first; carried on invoice /
// credit-note lines so a variant-configured line keeps its product identity.
import type { ProductConfigurationSelectionPayload } from '../../../generated/api/models/productConfigurationSelectionPayload';
// AR/AP document lifecycle + bank-move status value sets — single-sourced from
// the generated API models (JSON-schema-first) and re-exported below so callers
// keep importing them from this barrel; never hand-duplicate the unions.
import type { BankDepositStatus } from '../../../generated/api/models/bankDepositStatus';
import type { BankTransferStatus } from '../../../generated/api/models/bankTransferStatus';
import type { BillStatus } from '../../../generated/api/models/billStatus';
import type { InvoiceKind } from '../../../generated/api/models/invoiceKind';
import type { InvoiceStatus } from '../../../generated/api/models/invoiceStatus';
import type { DocumentPaymentStatus } from '../../../generated/api/models/documentPaymentStatus';
// Sharing relation value set — single-sourced from the config-enums binding
// (shared/config/permissions.config.json → relation.schema.json); never hand-copy the union.
import type { RelationType } from '../../../generated/config-enums';
// VAT presentation treatment value set — single-sourced from
// shared/config/tax_treatment.config.json (see config_enums_generator).
import type { TaxTreatment, RevenueRecognitionScheduleStatus } from '../../../generated/config-enums';
// Bank-rule condition field + operator value sets — single-sourced from
// shared/config/bank_rule_condition_*.config.json (see config_enums_generator).
import type {
  BankRuleConditionField,
  BankRuleConditionOperator,
} from '../../../generated/config-enums';

// Enums
export type AccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense' | 'direct_cost';
export type NormalBalance = 'debit' | 'credit';
// JournalEntryStatus is single-sourced from the generated API layer (JSON-schema-first:
// shared/schemas/accounting/journal_entry.schema.json → orval) — the JE response's
// `status` value set ('draft' | 'posted'). Imported (not just re-exported) so the
// ListJournalEntriesParams shape below can reference it locally; never hand-duplicate
// it (tools/check_typed_api_surface.py flags a hand-written copy as a ts-duplicate-type
// violation, matching how JournalEntryCreate is handled below).
import type { JournalEntryStatus } from '../../../generated/api/models/journalEntryStatus';
export type { JournalEntryStatus };
// Single-sourced from the generated API layer (enum schemas → orval). The
// FiscalPeriod response model surfaced these enums, so a hand-written union is a
// check_typed_api_surface.py ts-duplicate-type violation. `import type` + `export
// type` (not a bare `export … from`) so the names are BOTH re-exported AND in local
// scope for the FiscalPeriodCreate / ListFiscalPeriodsParams interfaces below —
// mirrors the JournalEntryStatus handling above.
import type { FiscalPeriodStatus } from '../../../generated/api/models/fiscalPeriodStatus';
import type { FiscalPeriodType } from '../../../generated/api/models/fiscalPeriodType';
export type { FiscalPeriodStatus, FiscalPeriodType };

// Journal-entry attachment reference — single-sourced from the generated API layer
// (JSON-schema-first: shared/schemas/accounting/journal_entry.schema.json → orval).
// Only `file_id` round-trips on the JE response; display metadata (filename, size,
// type) is resolved live from file_management by id — never denormalized onto the entry.
export type { JournalEntryAttachmentRef } from '../../../generated/api/models/journalEntryAttachmentRef';

// Account (full Chart-of-Accounts response) is single-sourced from the generated API
// layer (JSON-schema-first: shared/schemas/accounting/account.schema.json → orval). The
// GET/POST/PUT /accounts endpoints carry the `AccountResponse` response_model, so orval
// emits the full COA model (tenant_id, company_id, parent, balance, recursive children,
// tags, sort_code, …). The lightweight JE-line summary is a distinct generated type,
// `JournalEntryLineAccount` — never hand-duplicate either (tools/check_typed_api_surface.py
// flags a hand-written `Account` as a ts-duplicate-type violation).
export type { Account } from '../../../generated/api/models/account';

// AccountCreate / AccountUpdate are JSON-schema-first — generated from
// shared/schemas/accounting/{account_create,account_update}.schema.json and surfaced via
// the typed POST /accounts + PATCH /accounts/{id} request bodies. The Account *response*
// shape is likewise re-exported from the generated layer above (the /accounts read+write
// routes now carry the `AccountResponse` response_model), so nothing here drifts.
export type { AccountCreate } from '../../../generated/api/models/accountCreate';
export type { AccountUpdate } from '../../../generated/api/models/accountUpdate';

// Fiscal Period — single-sourced from the generated API layer (JSON-schema-first:
// shared/schemas/accounting/fiscal_period.schema.json → orval). The GET
// /fiscal-periods list + /fiscal-periods/{id} routes now carry response_models
// (FiscalPeriodListResponse / FiscalPeriodResponse), so orval emits the full
// FiscalPeriod model — including company_id (fiscal periods are per legal entity).
// A hand-written duplicate is a check_typed_api_surface.py ts-duplicate-type
// violation, so never re-declare it here.
export type { FiscalPeriod } from '../../../generated/api/models/fiscalPeriod';

export interface FiscalPeriodCreate {
  name: string;
  period_type: FiscalPeriodType;
  start_date: string;
  end_date: string;
  fiscal_year: number;
  period_number: number;
}

export interface GenerateFiscalYearRequest {
  fiscal_year: number;
  period_type?: FiscalPeriodType;
}

// JournalEntry is single-sourced from the generated API layer (JSON-schema-first:
// shared/schemas/accounting/journal_entry.schema.json → orval). The single-entry JE
// routes carry the `JournalEntryResponse` response_model, so the response schema now
// mirrors JournalEntry.to_dict() field-for-field (every always-present key is required;
// nullable values stay `T | null`; `exchange_rate` is null when no FX rate resolved).
// Never hand-duplicate it — tools/check_typed_api_surface.py flags a hand-written
// `JournalEntry` as a ts-duplicate-type violation.
export type { JournalEntry } from '../../../generated/api/models/journalEntry';

// JournalEntryLine is single-sourced from the generated API layer (JSON-schema-first:
// shared/schemas/accounting/journal_entry_line.schema.json → orval). The embedded
// lightweight account summary is the generated `JournalEntryLineAccount` (present only
// when the line is serialized with its account). Never hand-duplicate either —
// tools/check_typed_api_surface.py flags a hand-written copy as a ts-duplicate-type.
export type { JournalEntryLine } from '../../../generated/api/models/journalEntryLine';
export type { JournalEntryLineAccount } from '../../../generated/api/models/journalEntryLineAccount';

// JE create AND update request bodies (+ line) are single-sourced from the
// generated orval models (JSON Schema → Pydantic → OpenAPI → TS). Never
// hand-duplicate them — tools/check_typed_api_surface.py flags a hand-written
// `JournalEntryCreate` / `JournalEntryLineCreate` / `JournalEntryUpdate` as a
// `ts-duplicate-type` violation. `JournalEntryUpdate` mirrors create with every
// field optional (an omitted field is left unchanged); the entry currency /
// entry-level rate are fixed at creation and absent from the update body.
export type { JournalEntryCreate } from '../../../generated/api/models/journalEntryCreate';
export type { JournalEntryLineCreate } from '../../../generated/api/models/journalEntryLineCreate';
export type { JournalEntryUpdate } from '../../../generated/api/models/journalEntryUpdate';

// Reverse-a-posted-entry request body (POST /journal-entries/{id}/reverse).
// The generated model names it JournalEntryReverseRequest; re-exported under the
// shorter module-local alias used by the reverse dialog + hook.
export type { JournalEntryReverseRequest as JournalEntryReverse } from '../../../generated/api/models/journalEntryReverseRequest';


// ============== Analytic Dimensions ==============
// Catalog of analytic axes (Cost Center, Project, + tenant-defined) and their
// hierarchical values. See docs/modules/accounting/analytic-dimensions-design.md.

export type DimensionKind = 'cost_center' | 'project' | 'custom';

/** An analytic axis (Cost Center, Project, Department, …). Mirrors Dimension.to_dict(). */
export interface Dimension {
  id: string;
  tenant_id: string;
  company_id: string | null;
  code: string;
  name: string;
  description: string | null;
  dimension_kind: DimensionKind;
  is_hierarchical: boolean;
  applies_to_je: boolean;
  applies_to_bill: boolean;
  is_required: boolean;
  is_active: boolean;
  is_system: boolean;
  sort_order: number;
  settings: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

/** A value within a dimension, hierarchical. Mirrors DimensionValue.to_dict(). */
export interface DimensionValue {
  id: string;
  tenant_id: string;
  dimension_id: string;
  company_id: string | null;
  parent_value_id: string | null;
  code: string;
  name: string;
  description: string | null;
  depth: number;
  path: string;
  is_active: boolean;
  sort_order: number;
  settings: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

/** Assignment of one dimension value to a JE/bill line. At most one value per dimension. */
export interface LineDimensionAssignment {
  dimension_id: string;
  dimension_value_id: string;
}

export interface DimensionCreate {
  name: string;
  code?: string | null;
  company_id?: string | null;
  description?: string | null;
  is_hierarchical?: boolean;
  applies_to_je?: boolean;
  applies_to_bill?: boolean;
  is_required?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

export interface DimensionUpdate {
  name?: string;
  code?: string;
  company_id?: string | null;
  description?: string | null;
  is_hierarchical?: boolean;
  applies_to_je?: boolean;
  applies_to_bill?: boolean;
  is_required?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

export interface DimensionValueCreate {
  name: string;
  code?: string | null;
  parent_value_id?: string | null;
  company_id?: string | null;
  description?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

export interface DimensionValueUpdate {
  name?: string;
  code?: string;
  parent_value_id?: string | null;
  company_id?: string | null;
  description?: string | null;
  is_active?: boolean;
  sort_order?: number;
}


// Currency
export interface Currency {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  symbol: string;
  decimal_places: number;
  is_base_currency: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CurrencyCreate {
  code: string;
  name: string;
  symbol: string;
  decimal_places?: number;
  is_base_currency?: boolean;
}

// Exchange Rate
export interface ExchangeRate {
  id: string;
  tenant_id: string;
  from_currency: string;
  to_currency: string;
  rate_date: string;
  // NUMERIC(18,8) — serialized as a lossless string per the FX serialization
  // contract; coerce with Number(...) before arithmetic/display.
  rate: string;
  source: string;
  created_by: string | null;
  created_at: string;
}

export interface ExchangeRateCreate {
  from_currency: string;
  to_currency: string;
  rate_date: string;
  // Lossless decimal string — the server parses it straight to an exact
  // Numeric(18,8) Decimal (a JSON number would truncate through a binary float).
  rate: string;
  source?: string;
}

// Global market reference rate (tenant-less, read-only — written by the
// platform-wide FX sync task). Tenants resolve against these unless they have
// entered their own override row in accounting_exchange_rates.
export interface FxReferenceRate {
  id: string;
  base_currency: string;
  quote_currency: string;
  rate_date: string;
  rate: string; // NUMERIC(18,8) NOT NULL — lossless string (coerce with Number)
  source: string;
  created_at: string | null;
}

// The tenant's FX policy for one directed currency pair, merging the live market
// rate with whichever store governs the pair. One mode per pair: `manual` (a
// pinned rate, used verbatim), `custom` (this pair's own markup on the market
// rate), or `default` (a watch row inheriting the tenant-wide default markup).
// All re-exported from the generated API layer (JSON-schema-first:
// shared/schemas/accounting/fx_rate_policy*.schema.json) so they stay in lockstep
// with the typed /fx-rate-policies route bodies. `FxRatePolicyMode` is a const
// object + type — import the value to compare against a mode.
export { FxRatePolicyMode } from '../../../generated/api/models/fxRatePolicyMode';
export type { FxDefaultMarkupUpdate } from '../../../generated/api/models/fxDefaultMarkupUpdate';
export type { FxRatePolicy } from '../../../generated/api/models/fxRatePolicy';
export type { FxRatePolicyListData } from '../../../generated/api/models/fxRatePolicyListData';
export type { FxRatePolicyUpsert } from '../../../generated/api/models/fxRatePolicyUpsert';

// Chart-of-accounts initialization status + inheritance mode. Re-exported from
// the generated API layer (JSON-schema-first:
// shared/schemas/accounting/chart_status.schema.json + enums/coa_mode.schema.json)
// so the GET /accounts/status and POST /accounts/inherit response shapes stay in
// lockstep with the backend. Per-company + inheritance-aware fields are optional:
// they are absent on the legacy single-company (no active company) response.
export type { ChartStatus } from '../../../generated/api/models/chartStatus';
export type { CoaMode } from '../../../generated/api/models/coaMode';

// Reports
export interface TrialBalanceRow {
  account_id: string;
  account_number: string;
  account_name: string;
  account_type: AccountType;
  normal_balance: NormalBalance;
  debit_balance: number;
  credit_balance: number;
}

export interface TrialBalanceReport {
  as_of_date: string;
  accounts: TrialBalanceRow[];
  total_debit: number;
  total_credit: number;
  is_balanced: boolean;
}

// Discriminator on synthetic equity roll-in lines (undistributed net income
// closed into equity). Absent for real chart-of-accounts lines.
export type EquitySystemLine = 'current_year_earnings' | 'retained_earnings';

export interface BalanceSheetEntry {
  account_id: string;
  account_number: string;
  account_name: string;
  balance: number;
  // Present on synthetic equity roll-in lines; drives the i18n label instead of
  // account_name.
  system_line?: EquitySystemLine;
}

export interface BalanceSheetReport {
  as_of_date: string;
  assets: BalanceSheetEntry[];
  total_assets: number;
  liabilities: BalanceSheetEntry[];
  total_liabilities: number;
  equity: BalanceSheetEntry[];
  total_equity: number;
  total_liabilities_and_equity: number;
  is_balanced: boolean;
}

export interface IncomeStatementEntry {
  account_id: string;
  account_number: string;
  account_name: string;
  amount: number;
}

export interface IncomeStatementReport {
  start_date: string;
  end_date: string;
  revenue: IncomeStatementEntry[];
  total_revenue: number;
  direct_costs: IncomeStatementEntry[];
  total_direct_costs: number;
  gross_profit: number;
  expenses: IncomeStatementEntry[];
  total_expenses: number;
  net_income: number;
}

// Multi-company P2: consolidated reports
export interface ConsolidatedCompanyTrialBalance {
  company_id: string;
  company_name: string;
  base_currency: string | null;
  translated: boolean;
  report: TrialBalanceReport;
  translated_total_debit: number;
  translated_total_credit: number;
}

export interface ConsolidatedTrialBalanceReport {
  as_of_date: string;
  reporting_currency: string | null;
  companies: ConsolidatedCompanyTrialBalance[];
  grand_total_debit: number;
  grand_total_credit: number;
  is_balanced: boolean;
  untranslated_companies: string[];
}

export interface ConsolidatedCompanyBalanceSheet {
  company_id: string;
  company_name: string;
  base_currency: string | null;
  translated: boolean;
  report: BalanceSheetReport;
  translated_total_assets: number;
  translated_total_liabilities: number;
  translated_total_equity: number;
}

export interface ConsolidatedBalanceSheetReport {
  as_of_date: string;
  period_start_date: string;
  reporting_currency: string | null;
  companies: ConsolidatedCompanyBalanceSheet[];
  grand_total_assets: number;
  grand_total_liabilities: number;
  grand_total_equity: number;
  // Equity decomposition: other_equity + net_income_avg_rate + cta === grand_total_equity.
  grand_other_equity: number;
  grand_net_income_avg_rate: number;
  cumulative_translation_adjustment: number;
  grand_total_liabilities_and_equity: number;
  is_balanced: boolean;
  untranslated_companies: string[];
}

export interface ConsolidatedCompanyIncomeStatement {
  company_id: string;
  company_name: string;
  base_currency: string | null;
  translated: boolean;
  report: IncomeStatementReport;
  translated_total_revenue: number;
  translated_total_direct_costs: number;
  translated_total_expenses: number;
  translated_gross_profit: number;
  translated_net_income: number;
}

export interface ConsolidatedIncomeStatementReport {
  start_date: string;
  end_date: string;
  reporting_currency: string | null;
  companies: ConsolidatedCompanyIncomeStatement[];
  grand_total_revenue: number;
  grand_total_direct_costs: number;
  grand_gross_profit: number;
  grand_total_expenses: number;
  grand_net_income: number;
  untranslated_companies: string[];
}

export interface GeneralLedgerTransaction {
  date: string;
  entry_number: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

export interface GeneralLedgerReport {
  account_id: string;
  account_number: string;
  account_name: string;
  account_type: AccountType;
  normal_balance: NormalBalance;
  start_date: string;
  end_date: string;
  opening_balance: number;
  transactions: GeneralLedgerTransaction[];
  closing_balance: number;
}

// Account balance *report* (as-of-date snapshot with debit/credit totals) returned by
// GET /accounts/{id}/balance — distinct from the account entity's scalar `balance` field.
// Renamed off the bare `AccountBalance` to avoid colliding with the generated Account
// `balance`-field wrapper of that name (tools/check_typed_api_surface.py ts-duplicate-type).
export interface AccountBalanceReport {
  account_id: string;
  account_number: string;
  account_name: string;
  account_type: AccountType;
  normal_balance: NormalBalance;
  as_of_date: string;
  total_debit: number;
  total_credit: number;
  balance: number;
}

// API Response Types
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface DataResponse<T> {
  data: T;
  message?: string;
}

// Query Parameters
export interface ListAccountsParams {
  account_type?: AccountType;
  is_active?: boolean;
  is_header?: boolean;
  search?: string;
  tree?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Logical journal-entry buckets exposed by the entry-type filter, derived from
 * source_type/reversal columns. Single source of truth: the JSON-schema enum
 * shared/schemas/accounting/journal_entry_type.schema.json (→ orval). Re-exported
 * as both a type and a runtime value object so callers can enumerate the buckets.
 */
export { JournalEntryType };

export interface ListJournalEntriesParams {
  status?: JournalEntryStatus;
  fiscal_period_id?: string;
  start_date?: string;
  end_date?: string;
  account_id?: string;
  debit_account_id?: string;
  credit_account_id?: string;
  amount_min?: number;
  amount_max?: number;
  /** Logical entry type derived from source_type/reversal columns. */
  entry_type?: JournalEntryType;
  created_by?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ListFiscalPeriodsParams {
  fiscal_year?: number;
  period_type?: FiscalPeriodType;
  status?: FiscalPeriodStatus;
  /** Scope periods to one company (periods are per legal entity). Defaults to
   * the active-company header scope when omitted. */
  company_id?: string;
  limit?: number;
  offset?: number;
}

// ============== Friendly Layer Types ==============

// Status enums
/**
 * Stored lifecycle status values — pure lifecycle (ERP [37] contract). The
 * payment axis ('partially_paid'/'paid') lives on `DocumentPaymentStatus`, not
 * here. 'overdue' is NOT stored either; use the is_overdue field.
 * Single-sourced from the generated API model (imported above); re-exported so
 * InvoiceDisplayStatus below can reference it locally.
 */
export type { InvoiceStatus };
/** Display-only — includes derived 'overdue' for badge rendering. */
export type InvoiceDisplayStatus = InvoiceStatus | 'overdue';
/**
 * Stored lifecycle status values — pure lifecycle (ERP [37] contract). The
 * payment axis lives on `DocumentPaymentStatus`. 'overdue' is NOT stored; use
 * the is_overdue field.
 * Single-sourced from the generated API model (imported above); re-exported so
 * BillDisplayStatus below can reference it locally.
 */
export type { BillStatus };
/** Display-only — includes derived 'overdue' for badge rendering and virtual filter. */
export type BillDisplayStatus = BillStatus | 'overdue';
/**
 * ERP P2 [37]: derived payment rollup on AR invoices + AP bills, orthogonal to
 * the lifecycle status. Recomputed server-side from amount_paid/amount_applied
 * vs amount_due. Read this for the payment axis — NOT status IN ('paid',...).
 * Single-sourced from the JSON schema (`document_payment_status.schema.json`);
 * distinct from the Payment-entity `PaymentStatus` (cleared/voided).
 */
export type { DocumentPaymentStatus };
export type PaymentType = 'received' | 'made';
// Finding [117]: payments settle to 'cleared'. The legacy 'completed' value was
// backfilled to 'cleared' and dropped from the DB CHECK by the contract change.
export type PaymentStatus = 'cleared' | 'voided';
export type TaxType = 'sales' | 'purchase' | 'both';
// VAT presentation treatment. A non-'standard' value renders a legend on the
// invoice/PDF and marks a no-VAT-charged supply. Single-sourced — re-exported
// from the generated config-enums binding.
export type { TaxTreatment };

// Tax Rate
export interface TaxRate {
  id: string;
  tenant_id: string;
  name: string;
  rate: number;
  tax_type: TaxType;
  is_inclusive: boolean;
  tax_treatment: TaxTreatment;
  tax_collected_account_id: string | null;
  tax_paid_account_id: string | null;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  components?: TaxRateComponent[];
}

export interface TaxRateComponent {
  id: string;
  tax_rate_id: string;
  name: string;
  rate: number;
  account_id: string;
  sort_order: number;
}

export interface TaxRateCreate {
  name: string;
  rate: number;
  tax_type?: TaxType;
  is_inclusive?: boolean;
  tax_treatment?: TaxTreatment;
  tax_collected_account_id?: string;
  tax_paid_account_id?: string;
  is_default?: boolean;
}

export interface TaxRateUpdate {
  name?: string;
  rate?: number;
  tax_type?: TaxType;
  is_inclusive?: boolean;
  tax_treatment?: TaxTreatment;
  tax_collected_account_id?: string | null;
  tax_paid_account_id?: string | null;
  is_default?: boolean;
  is_active?: boolean;
}

export interface ListTaxRatesParams {
  is_active?: boolean;
  tax_type?: TaxType;
  search?: string;
  limit?: number;
  offset?: number;
}

// Invoice
export interface InvoiceLine {
  id: string;
  invoice_id: string;
  line_number: number;
  description: string;
  /** OFF-7: offerings supertype handle (product|bundle) — the sole catalog ref; product_id dropped. */
  offering_id: string | null;
  packaging_id: string | null;
  /** ProductReference identity carried from the source sale line so a credit note
   *  matches the original product/variant. Null on GL-only free-text lines. */
  variant_id?: string | null;
  configuration_selections?: ProductConfigurationSelectionPayload[] | null;
  quantity_in_packaging?: number | null;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  line_total: number;
  tax_rate_id: string | null;
  tax_amount: number;
  /** Inclusive-tax ownership marker: true = stored unit_price is GROSS (VAT extracted),
   *  false = already NET / exclusive. Round-tripped on save so re-editing a generated
   *  inclusive invoice does not re-extract the VAT (WI fc8f6615). */
  prices_include_tax: boolean;
  /** VAT treatment snapshot captured at invoice creation; null = ordinary supply / no legend. */
  tax_treatment: TaxTreatment | null;
  account_id: string;
  /** Deferred-revenue service period (feature gap [33]); both set + a configured
   *  deferred-revenue account defers this line's revenue over the period. */
  service_period_start?: string | null;
  service_period_end?: string | null;
}

/** One (invoice line x calendar month) period of a deferred-revenue schedule. */
export interface RevenueRecognitionSchedule {
  id: string;
  invoice_id: string;
  invoice_line_id: string;
  company_id: string;
  period: string;
  amount: number;
  currency_code: string;
  amount_in_base: number;
  exchange_rate: string;
  status: RevenueRecognitionScheduleStatus;
  journal_entry_id: string | null;
  line_description?: string | null;
  revenue_account_name?: string | null;
}

/** One month of the deferred-revenue liability roll-forward. */
export interface DeferredRevenueRollforwardPeriod {
  period: string;
  opening: number;
  deferred: number;
  recognized: number;
  closing: number;
}

export interface DeferredRevenueRollforward {
  account_id: string | null;
  account_name: string | null;
  opening_balance: number;
  periods: DeferredRevenueRollforwardPeriod[];
  closing_balance: number;
}

// AR document kind — single-sourced from the generated InvoiceKind (JSON-schema-first:
// invoice / credit_memo_adjustment / credit_memo_refundable / finance_charge / nsf_fee).
// The previous hand-written union had drifted (debit_memo / proforma / recurring_template
// were never emitted by the backend); re-export so it can't diverge again.
export type { InvoiceKind };

/** Invoice kinds that represent credit memos — import from here to avoid drift. */
export const CREDIT_MEMO_KINDS: readonly InvoiceKind[] = ['credit_memo_adjustment', 'credit_memo_refundable'];

export interface Invoice {
  id: string;
  tenant_id: string;
  /** Legal entity this invoice belongs to (companies.id). S-pattern, locked once posted. */
  company_id: string;
  invoice_number: string;
  customer_id: string | null;
  customer_name: string | null;
  contact_id: string | null;
  employee_id: string | null;
  issue_date: string;
  due_date: string;
  status: InvoiceStatus;
  /** ERP P2 [37]: derived payment rollup, orthogonal to lifecycle status. */
  payment_status: DocumentPaymentStatus;
  /** Derived signal: due_date < today AND amount_due > 0 AND status is open. Never stored. */
  is_overdue: boolean;
  /** Derived-and-cached intercompany rollup: customer org is one of the tenant's own companies. */
  is_intercompany: boolean;
  currency_code: string;
  // null when no FX rate was resolved — render blank, never a phantom 1:1.
  exchange_rate: string | null;
  subtotal: number;
  tax_total: number;
  total: number;
  /** ERP P13: row-level base-currency snapshot of `total` (null pre-snapshot or base-currency rows). */
  total_in_base: number | null;
  amount_paid: number;
  /** SEC-1: credit settled by applied credit memos. Regular invoice → reduces
   * amount_due with amount_paid; credit memo → absolute credit consumed. */
  amount_applied: number;
  amount_due: number;
  /** Normalized payment-terms value object (ERP P3). */
  payment_terms_id: string | null;
  payment_terms_basis: PaymentTermsBasis | null;
  payment_terms_days: number | null;
  reference: string | null;
  notes: string | null;
  internal_notes: string | null;
  kind: InvoiceKind;
  related_invoice_ids: string[];
  /**
   * SEC-1: navigable projection of related_invoice_ids resolved by the
   * GET /invoices/{id} endpoint. Symmetric — a credit memo lists the invoices
   * it credits; an invoice lists the credit memos issued against it.
   */
  related_invoices?: RelatedInvoiceLink[];
  source_type: string | null;
  source_id: string | null;
  /** Order-to-Invoice (Contract D): the sales-order goods-OUT shipment this AR invoice bills. */
  source_shipment_id: string | null;
  /** TMS B1: the freight shipment this AR invoice represents as job revenue (forwarder sell-side). */
  freight_shipment_id: string | null;
  journal_entry_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  lines: InvoiceLine[];
  /** Cross-cutting tags framework — tags assigned to this invoice. */
  tags?: TagSummary[];
  /**
   * Sharing: the calling user's best relation under a restricted visibility mode.
   * Null/absent in everyone mode (legacy full access) — drives the Share
   * affordances + SharedWithMeChip. Schema-first (mirrors Bill's user_relation).
   */
  user_relation?: RelationType | null;
}

/** SEC-1: a navigable link to a related AR document (credit memo ↔ original). */
export interface RelatedInvoiceLink {
  id: string;
  invoice_number: string;
  kind: InvoiceKind;
  status: InvoiceStatus;
}

export interface InvoiceLineCreate {
  description: string;
  account_id: string;
  quantity?: number;
  unit_price: number;
  discount_percent?: number;
  tax_rate_id?: string;
  tax_amount?: number;
  /** Inclusive-tax ownership marker. Omit/undefined → backend falls back to the tax
   *  rate's inclusive default (correct for new lines); send the stored value for lines
   *  loaded off a generated invoice so the net price is not re-extracted (WI fc8f6615). */
  prices_include_tax?: boolean;
  /** OFF-7: offerings supertype handle — the sole catalog ref on submit. */
  offering_id?: string;
  packaging_id?: string;
  /** ProductReference identity — carried through a partial credit note so the
   *  credit line keeps the original sale's product/variant identity. */
  variant_id?: string | null;
  configuration_selections?: ProductConfigurationSelectionPayload[] | null;
  quantity_in_packaging?: number | null;
  /** Deferred-revenue service period — carried through a partial credit note so a
   *  credited deferred-revenue line keeps its recognition window. */
  service_period_start?: string | null;
  service_period_end?: string | null;
}

/** One invoice a bulk send/void could not apply, with a machine-usable reason. */
export interface BulkInvoiceSkip {
  id: string;
  reason: string;
}

/** Result of a bulk send/void: ids that succeeded and those skipped with a reason. */
export interface BulkInvoiceActionResult {
  succeeded: string[];
  skipped: BulkInvoiceSkip[];
}

export interface InvoiceCreate {
  due_date?: string;  // Ignored when payment_terms_basis is provided (terms win).
  /** Owning legal entity. Required when tenant has multiple companies. */
  company_id?: string;
  lines?: InvoiceLineCreate[];
  issue_date?: string;
  customer_id?: string;
  customer_name?: string;
  contact_id?: string;
  employee_id?: string;
  currency_code?: string;
  /** DECIMAL(18,8) FX rate serialized losslessly as a string (never a binary
   * float) to match the create-invoice request contract; null/omitted → 1:1. */
  exchange_rate?: string | null;
  /** Normalized payment-terms value object (ERP P3). */
  payment_terms_id?: string | null;
  payment_terms_basis?: PaymentTermsBasis | null;
  payment_terms_days?: number | null;
  reference?: string;
  notes?: string;
  internal_notes?: string;
  /** Cross-cutting tags framework — full-replacement set of tag ids. */
  tag_ids?: string[];
}

export interface InvoiceUpdate {
  due_date?: string;
  issue_date?: string;
  customer_id?: string;
  customer_name?: string;
  contact_id?: string;
  employee_id?: string;
  currency_code?: string;
  /** Normalized payment-terms value object (ERP P3). */
  payment_terms_id?: string | null;
  payment_terms_basis?: PaymentTermsBasis | null;
  payment_terms_days?: number | null;
  reference?: string;
  notes?: string;
  internal_notes?: string;
  lines?: InvoiceLineCreate[];
  /** Cross-cutting tags: omit to leave unchanged, [] to clear. */
  tag_ids?: string[];
}

export interface ListInvoicesParams {
  /** 'overdue' is a virtual filter (sent + due_date < today), not a stored status. */
  status?: InvoiceDisplayStatus;
  /** ERP [37]: filter on the derived payment axis, orthogonal to lifecycle status. */
  payment_status?: DocumentPaymentStatus;
  /** Narrow to open overdue invoices at least N days past due (dashboard "60+ days" CTA). */
  min_days_overdue?: number;
  customer_id?: string;
  /** Intercompany detection rollup: customer org is one of the tenant's own companies. */
  is_intercompany?: boolean;
  search?: string;
  date_from?: string;
  date_to?: string;
  amount_min?: number;
  amount_max?: number;
  /** Cross-cutting tags framework — comma-separated tag UUIDs. */
  tags?: string;
  tags_mode?: 'any' | 'all';
  /** §4.1 "narrow to mine": `'own'` scopes an admin to their own owner/shared set. */
  admin_scope?: string;
  limit?: number;
  offset?: number;
}

// Payment
export interface PaymentAllocation {
  id: string;
  payment_id: string;
  allocated_to_type: string;
  allocated_to_id: string;
  amount: number;
  /** Target document's display number (e.g. "BILL-0007"), joined server-side so
   * the payment detail dialog can label + link the allocation. Null when the
   * target type has no resolvable number (e.g. credit notes). */
  allocated_to_number?: string | null;
}

export interface Payment {
  id: string;
  tenant_id: string;
  company_id: string;
  payment_number: string;
  payment_type: PaymentType;
  contact_id: string | null;
  contact_name: string | null;
  payment_date: string;
  currency_code: string;
  // null when no FX rate was resolved — render blank, never a phantom 1:1.
  exchange_rate: string | null;
  amount: number;
  deposit_to_account_id: string;
  deposit_to_account_name: string | null;
  payment_method: string | null;
  reference: string | null;
  notes: string | null;
  status: PaymentStatus;
  fx_gain_loss: number;
  fx_gain_loss_account_id: string | null;
  journal_entry_id: string | null;
  source_bill_id: string | null;
  /** Reimbursement / paid-on-our-behalf: set when this payment settles a bill paid
   * by a third party (owner's/employee's personal card). Funded from Reimbursements
   * Payable; drives the "Paid by {payer}" badge. Null for ordinary payments. */
  payer_entity_table: PayerEntityTable | null;
  payer_entity_id: string | null;
  payer_name: string | null;
  /** T20B: bank transaction number (TNUM) for bank-statement reconciliation. */
  bank_transaction_number: string | null;
  receipt_file_id: string | null;
  receipt_url: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  allocations: PaymentAllocation[];
  /** Cross-cutting tags framework — tags assigned to this payment. */
  tags?: TagSummary[];
}

export interface PaymentAllocationCreate {
  allocated_to_type: string;
  allocated_to_id: string;
  amount: number;
}

/** A payment applied to a specific invoice — the invoice-detail history row.
 * `allocated_amount` is the slice applied to THIS invoice (a receipt can split
 * across several). Allocations are omitted from this projection. */
export type InvoicePayment = Omit<Payment, 'allocations'> & { allocated_amount: number };

export interface PaymentCreate {
  payment_type: PaymentType;
  amount: number;
  /** Owning legal entity. Required when tenant has multiple companies. */
  company_id?: string;
  deposit_to_account_id: string;
  payment_date?: string;
  contact_id?: string;
  contact_name?: string;
  currency_code?: string;
  /** DECIMAL(18,8) FX rate serialized losslessly as a string (never a binary
   * float) to match the create-payment request contract; null/omitted → 1:1. */
  exchange_rate?: string | null;
  payment_method?: string;
  payment_method_kind?: 'cash' | 'check' | 'ach' | 'wire' | 'card' | 'digital_wallet' | 'international_wire' | 'other';
  reference?: string;
  notes?: string;
  /** T20B: bank transaction number (TNUM) for bank-statement reconciliation. */
  bank_transaction_number?: string;
  allocations?: PaymentAllocationCreate[];
  /** Cross-cutting tags framework — full-replacement set of tag ids. */
  tag_ids?: string[];
}

export interface ListPaymentsParams {
  payment_type?: PaymentType;
  status?: PaymentStatus;
  search?: string;
  start_date?: string;
  end_date?: string;
  /** Cross-cutting tags framework — comma-separated tag UUIDs. */
  tags?: string;
  tags_mode?: 'any' | 'all';
  limit?: number;
  offset?: number;
}

// T20B: landed-cost allocation methods — single-sourced from
// shared/config/landed_cost_allocation_methods.config.json via config-enums.ts.
export type AllocationMethod = LandedCostAllocationMethod;

// T20B: tenant-scoped landed-cost cost-type catalog (FREIGHT, DUTY, …).
export interface LandedCostCategory {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  default_allocation_method: AllocationMethod;
  gl_account_id: string | null;
  is_active: boolean;
  sort_order: number | null;
  created_at: string;
  updated_at: string;
}

// T20B: payload to link (or unlink) a bill to an inbound freight shipment.
export interface BillShipmentLink {
  freight_shipment_id: string | null;
  landed_cost_category_id: string | null;
  allocation_method: AllocationMethod | null;
}

// P2P-1: a confirmed goods receipt not yet on any bill — row in the
// vendor→unbilled-receipts picker used to build a bill from receipts.
export interface UnbilledReceiptSummary {
  id: string;
  receipt_number: string;
  received_date: string;
  purchase_order_id: string;
  po_number: string | null;
  line_count: number;
  currency_code: string;
  total_cents: number;
}

// Bill (unified: merged bills; negative = vendor credit note).
// Bill, BillKind, and BillLine are JSON-schema-first — generated from
// shared/schemas/accounting/{bill,bill_line}.schema.json + enums/bill_kind.schema.json
// and surfaced to TS via the typed GET /bills and GET /bills/{id} response_models.
// The serialized shape mirrors Bill.to_dict() plus the route-attached `tags`
// projection and `customs_release_status` rollup. `lines` is optional because
// the list endpoint serializes with include_lines=False.
export type { Bill } from '../../../generated/api/models/bill';
export type { BillKind } from '../../../generated/api/models/billKind';
export type { BillLine } from '../../../generated/api/models/billLine';

export interface BillLineCreate {
  description: string;
  account_id: string;
  quantity?: number;
  unit_price: number;
  discount_percent?: number;
  tax_rate_id?: string;
  tax_amount?: number;
  /** Inclusive-tax ownership marker (WI fc8f6615). True = unit_price is GROSS
   * (VAT extracted); false = already NET / exclusive; omit to fall back to the
   * rate's inclusive default. The detail-page editor round-trips the stored value
   * so re-saving a draft bill does not re-extract the VAT or pick up a later edit
   * to the rate's inclusive flag. */
  prices_include_tax?: boolean;
  /** OFF-7: offerings supertype handle — the sole catalog ref on submit. */
  offering_id?: string;
  packaging_id?: string;
}

export interface BillCreate {
  lines: BillLineCreate[];
  /** Owning legal entity. Required when tenant has multiple companies. */
  company_id?: string;
  is_paid_immediately?: boolean;
  issue_date?: string;
  due_date?: string;  // Ignored when payment_terms_basis is provided (terms win).
  vendor_name?: string;
  vendor_id?: string;
  vendor_number?: string;
  description?: string;
  currency_code?: string;
  /** DECIMAL(18,8) FX rate serialized losslessly as a string (never a binary
   * float) to match the create-bill request contract; null/omitted → 1:1. */
  exchange_rate?: string | null;
  /** Normalized payment-terms value object (ERP P3). */
  payment_terms_id?: string | null;
  payment_terms_basis?: PaymentTermsBasis | null;
  payment_terms_days?: number | null;
  reference?: string;
  notes?: string;
  paid_from_account_id?: string;
  payment_method?: string;
  receipt_file_id?: string;
  receipt_url?: string;
  employee_id?: string;
  /** Cross-cutting tags framework — full-replacement set of tag ids. */
  tag_ids?: string[];
}

export interface BillUpdate {
  lines?: BillLineCreate[];
  is_paid_immediately?: boolean;
  issue_date?: string;
  due_date?: string;
  vendor_name?: string;
  vendor_id?: string;
  vendor_number?: string;
  description?: string;
  employee_id?: string;
  currency_code?: string;
  exchange_rate?: number;
  /** Normalized payment-terms value object (ERP P3). */
  payment_terms_id?: string | null;
  payment_terms_basis?: PaymentTermsBasis | null;
  payment_terms_days?: number | null;
  reference?: string;
  notes?: string;
  paid_from_account_id?: string;
  payment_method?: string;
  receipt_file_id?: string;
  receipt_url?: string;
  /** Cross-cutting tags: omit to leave unchanged, [] to clear. */
  tag_ids?: string[];
}

export interface ListBillsParams {
  /**
   * Lifecycle status filter. A single `BillDisplayStatus`, or a comma-joined set
   * (e.g. the AP-aging drill-through `due,overdue`) — the API OR-combines the
   * tokens at the query level. 'overdue' is a virtual filter (due + due_date <
   * today + open balance), not a stored status.
   */
  status?: string;
  /** ERP [37]: filter on the derived payment axis, orthogonal to lifecycle status. */
  payment_status?: DocumentPaymentStatus;
  vendor_id?: string;
  is_paid_immediately?: boolean;
  /** Intercompany detection rollup: vendor org is one of the tenant's own companies. */
  is_intercompany?: boolean;
  search?: string;
  date_from?: string;
  date_to?: string;
  /** Due-date window (AP-aging bucket drill-through) — filters on due_date, not issue_date. */
  due_from?: string;
  due_to?: string;
  /** With due_from/due_to, also include undated bills (AP-aging "current" bucket drill-through). */
  include_undated_due?: boolean;
  amount_min?: number;
  amount_max?: number;
  /** Cross-cutting tags framework — comma-separated tag UUIDs. */
  tags?: string;
  tags_mode?: 'any' | 'all';
  /** §4.1 "narrow to mine": `'own'` scopes an admin to their own owner/shared set. */
  admin_scope?: string;
  limit?: number;
  offset?: number;
}

// ============================================================
// BANK ACCOUNTS
// ============================================================

export type InstitutionType = 'checking' | 'savings' | 'credit_card' | 'paypal' | 'other';
export type CardNetwork = 'visa' | 'mastercard' | 'amex' | 'discover';

// ============================================================
// REIMBURSEMENT PAYERS (owner/personal & third-party card payers)
// ============================================================

// The payer value sets and payloads are all single-sourced from the generated API
// layer (JSON-schema-first: shared/schemas/accounting/payer_*.schema.json and
// reimbursement_clear_*.schema.json → orval), so they stay in lockstep with the
// typed /reimbursements/* route bodies. Never hand-duplicate them —
// tools/check_typed_api_surface.py flags a hand-written copy as a
// ts-duplicate-type violation.
//
// `CardOwnership` (a `personal` card belongs to a payer; a `business` card
// settles from a GL account), `PayerEntityTable` (the physical table backing a
// polymorphic payer reference) and `PayerLedgerEntryType` (subledger entry type)
// are each a const object + type — import the value to enumerate the members or
// compare against one. CardOwnership/PayerEntityTable are imported (not just
// re-exported) so the BankAccount* shapes below can reference them locally.
export { CardOwnership, PayerEntityTable };
export { PayerLedgerEntryType } from '../../../generated/api/models/payerLedgerEntryType';

// `PayerBalance` — one payer's outstanding "Due to <payer>" balance in a given
// currency (positive = owed). `PayerReimbursementLedgerEntry` — an append-only
// row of the subledger; `amount` is signed (+ business owes payer (accrual) /
// − paid back (clearing)). Both carry every field `to_dict()` emits: the nullable
// columns (payer_name, description, bill_id, payment_id, journal_entry_id,
// created_by, created_at) are required keys with nullable values, mirroring the
// columns verbatim, so a legacy null can't trip response_model validation.
// `PayerTransactionsResponse` (one payer's full ledger plus its per-currency
// outstanding balances) is the generated transactions envelope under the name the
// module already uses.
export type { PayerBalance } from '../../../generated/api/models/payerBalance';
export type { PayerReimbursementLedgerEntry } from '../../../generated/api/models/payerReimbursementLedgerEntry';
export type { ReimbursementClearBatchRequest } from '../../../generated/api/models/reimbursementClearBatchRequest';
export type { ReimbursementClearRequest } from '../../../generated/api/models/reimbursementClearRequest';
export type { ReimbursementPayerTransactionsResponse as PayerTransactionsResponse } from '../../../generated/api/models/reimbursementPayerTransactionsResponse';

export interface ListPayerBalancesParams {
  company_id?: string;
  include_zero?: boolean;
}

export interface ListPayerTransactionsParams {
  company_id?: string;
  limit?: number;
  offset?: number;
}

export interface BankAccount {
  id: string;
  tenant_id: string;
  // Owning legal entity (multi-company scope). Surfaced so the list can show a
  // company indicator under the "All companies" scope. Mirrors BankAccount.to_dict().
  company_id: string;
  // Null for a personal card (a payer's card): it carries no business GL account.
  account_id: string | null;
  institution_type: InstitutionType;
  bank_name: string | null;
  account_name: string;
  account_number_masked: string | null;
  currency_code: string;
  country_code: string | null;
  routing_number: string | null;
  sort_code: string | null;
  // Full IBAN is encrypted at rest and never returned — only the last-4 remnant.
  iban_last4: string | null;
  swift_bic: string | null;
  account_holder_name: string | null;
  credit_limit: number | null;
  card_network: CardNetwork | null;
  statement_closing_day: number | null;
  current_balance: number;
  feed_provider: string | null;
  feed_connection_id: string | null;
  last_feed_date: string | null;
  // Reimbursement payers: a `personal` card belongs to a payer (owner/employee/
  // contractor/third party) whose spend the business owes back; a `business` card
  // settles from a business GL account. Payer fields are set only when personal.
  card_ownership: CardOwnership;
  payer_entity_table: PayerEntityTable | null;
  payer_entity_id: string | null;
  payer_name: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// The bank-account create/update request bodies are the orval-generated types
// driven by bank_account_{create,update}_request.schema.json — never hand-written,
// so a schema change (rename, tightened type, new required field) can't drift a
// manual copy silently (the old hand-written `BankAccountCreate`/`BankAccountUpdate`
// interfaces this replaced could). Re-exported from `../types` so the module keeps
// a single import surface. Their optional fields are nullable (`string | null` etc.)
// because the JSON schema marks them nullable; the create/update flows only ever
// send `value | undefined`, which the wider generated types accept.
export type { BankAccountCreateRequest } from '../../../generated/api/models/bankAccountCreateRequest';
export type { BankAccountUpdateRequest } from '../../../generated/api/models/bankAccountUpdateRequest';

export interface ListBankAccountsParams {
  institution_type?: InstitutionType;
  is_active?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

// ============================================================
// BANK TRANSACTIONS & RECONCILIATION
// ============================================================

export type ReconciliationStatus = 'unreconciled' | 'matched' | 'created' | 'excluded';
export type MatchedType = 'payment' | 'invoice' | 'bill' | 'journal_entry';

export interface BankTransaction {
  id: string;
  tenant_id: string;
  bank_account_id: string;
  transaction_date: string;
  amount: number;
  description: string | null;
  reference: string | null;
  payee: string | null;
  import_id: string | null;
  imported_at: string | null;
  reconciliation_status: ReconciliationStatus;
  matched_type: string | null;
  matched_id: string | null;
  reconciled_at: string | null;
  reconciled_by: string | null;
  counterparty_id: string | null;
  counterparty_type: 'company' | 'contact' | null;
  counterparty_name: string | null;
  counterparty_confidence: number | null;
  category_account_id: string | null;
  category_confidence: number;
  journal_entry_id: string | null;
  category_account_name?: string | null;
  created_at: string;
}

export interface ListBankTransactionsParams {
  bank_account_id?: string;
  /** Single status, or multiple statuses (sent comma-separated) for the per-account register. */
  reconciliation_status?: ReconciliationStatus | ReconciliationStatus[];
  start_date?: string;
  end_date?: string;
  search?: string;
  offset?: number;
  limit?: number;
}

export interface MatchCandidate {
  matched_type: string;
  matched_id: string;
  amount: number;
  date: string;
  description: string;
  score: number;
  currency_code: string | null;
}

export interface ReconciliationSummary {
  total: number;
  unreconciled: number;
  matched: number;
  created: number;
  excluded: number;
  unreconciled_deposits: number;
  unreconciled_withdrawals: number;
  active_total: number;
  counterparty_resolved: number;
  counterparty_tentative: number;
  counterparty_missing: number;
  category_resolved: number;
  category_tentative: number;
  category_missing: number;
}

export interface ImportResult {
  imported: number;
  duplicates: number;
  parser: string;
  import_id?: string;
  errors?: string[];
}

export interface AutoReconcileResult {
  auto_matched: number;
  rule_matched: number;
  total_unreconciled: number;
}

export interface RematchResult {
  rule_applied: boolean;
  counterparty_identified: boolean;
  category_inferred: boolean;
  transaction: BankTransaction;
}

// ============================================================
// BANK RULES
// ============================================================

export type RuleActionType = 'categorize' | 'match_contact';

// P9 JSONB match predicate — a flat list of AND-combined conditions.
// Field + operator value sets are single-sourced (imported above); re-export them
// so module consumers keep importing from the accounting types barrel.
export type { BankRuleConditionField, BankRuleConditionOperator };
export type BankRuleDirection = 'inflow' | 'outflow';

export interface BankRuleCondition {
  field: BankRuleConditionField;
  op: BankRuleConditionOperator;
  // string for text/direction, number for amount ops, [min, max] for amount 'between'.
  value: string | number | number[];
}

export interface BankRuleMatchPredicate {
  conditions: BankRuleCondition[];
}

export interface BankRule {
  id: string;
  tenant_id: string;
  company_id: string;
  name: string;
  priority: number;
  is_active: boolean;
  match_predicate: BankRuleMatchPredicate | null;
  action_type: RuleActionType;
  target_account_id: string | null;
  target_contact_id: string | null;
  target_tax_rate_id: string | null;
  times_applied: number;
  created_at: string;
  updated_at: string;
}

export interface BankRuleCreate {
  // Optional; the API resolves company from body > X-Active-Company-Id > primary.
  // The dialog sends it explicitly so a multi-company "All companies" scope (no
  // header) doesn't 400 with "company_id is required".
  company_id?: string | null;
  name: string;
  priority?: number;
  match_predicate: BankRuleMatchPredicate;
  action_type: RuleActionType;
  target_account_id?: string | null;
  target_contact_id?: string | null;
  target_tax_rate_id?: string | null;
}

export interface BankRuleUpdate {
  name?: string;
  priority?: number;
  is_active?: boolean;
  match_predicate?: BankRuleMatchPredicate;
  action_type?: RuleActionType;
  target_account_id?: string | null;
  target_contact_id?: string | null;
  target_tax_rate_id?: string | null;
}

export interface ListBankRulesParams {
  is_active?: boolean;
  search?: string;
  offset?: number;
  limit?: number;
}

export interface BankRulePreviewRequest {
  company_id?: string | null;
  match_predicate: BankRuleMatchPredicate;
}

export interface BankRulePreviewResult {
  match_count: number;
  // True when the company's unreconciled feed exceeds the backend scan cap, so
  // match_count is a lower bound over the scanned window rather than an exact total.
  scan_truncated: boolean;
}

export interface BankRuleApplyResult {
  applied_count: number;
}

// ── Default-dimension rules (P9 rules engine) ────────────────────────
// Dimension / DimensionValue live in the "Analytic Dimensions" section above.

export type DimensionRuleAccountType =
  | 'asset'
  | 'liability'
  | 'equity'
  | 'revenue'
  | 'expense'
  | 'direct_cost';

export type DimensionRuleLineType = 'je' | 'bill';

export interface DimensionDefaultRulePredicate {
  account_id?: string | null;
  vendor_id?: string | null;
  account_type?: DimensionRuleAccountType | null;
  line_type?: DimensionRuleLineType | null;
}

export interface DimensionDefaultRule {
  id: string;
  tenant_id: string;
  company_id: string | null;
  name: string;
  predicate: DimensionDefaultRulePredicate;
  dimension_id: string;
  dimension_value_id: string;
  priority: number;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
  /** Enrichment from the list endpoint (batch-loaded display names). */
  dimension_name?: string | null;
  dimension_value_name?: string | null;
}

export interface DimensionDefaultRuleCreate {
  name: string;
  company_id?: string | null;
  predicate?: DimensionDefaultRulePredicate;
  dimension_id: string;
  dimension_value_id: string;
  priority?: number;
  is_active?: boolean;
}

export interface DimensionDefaultRuleUpdate {
  name?: string;
  company_id?: string | null;
  predicate?: DimensionDefaultRulePredicate;
  dimension_id?: string;
  dimension_value_id?: string;
  priority?: number;
  is_active?: boolean;
}

export interface ListDimensionDefaultRulesParams {
  company_id?: string;
  include_inactive?: boolean;
}

// ── Tax Determination Rules (jurisdiction-based tax rate selection) ──────

export type TaxDeterminationType = 'sales' | 'purchase' | 'both';

/** Eligibility predicate — AND across keys, OR within a list, absent key = wildcard. */
export interface TaxDeterminationPredicate {
  ship_to_countries?: string[];
  ship_to_regions?: string[];
  product_tax_categories?: string[];
  customer_ids?: string[];
}

export interface TaxDeterminationRule {
  id: string;
  tenant_id: string;
  company_id: string;
  name: string;
  priority: number;
  tax_type: TaxDeterminationType;
  predicate: TaxDeterminationPredicate;
  tax_rate_id: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface TaxDeterminationRuleCreate {
  company_id?: string;
  name: string;
  priority?: number;
  tax_type?: TaxDeterminationType;
  predicate?: TaxDeterminationPredicate;
  tax_rate_id?: string | null;
  is_active?: boolean;
}

export interface TaxDeterminationRuleUpdate {
  name?: string;
  priority?: number;
  tax_type?: TaxDeterminationType;
  predicate?: TaxDeterminationPredicate;
  tax_rate_id?: string | null;
  clear_tax_rate?: boolean;
  is_active?: boolean;
}

export interface ListTaxDeterminationRulesParams {
  tax_type?: TaxDeterminationType;
  is_active?: boolean;
}

/** Preview-endpoint request — the document-line context to resolve a rate for. */
export interface TaxDeterminationResolveRequest {
  company_id?: string;
  tax_type?: 'sales' | 'purchase';
  ship_to_country?: string | null;
  ship_to_region?: string | null;
  product_tax_category?: string | null;
  customer_id?: string | null;
  explicit_tax_rate_id?: string | null;
}

export interface TaxDeterminationResult {
  tax_rate_id: string | null;
  source: 'override' | 'rule' | 'default' | 'none';
  matched_rule_id: string | null;
  matched_rule_name: string | null;
  // VAT presentation treatment of the resolved rate; null when no rate resolved.
  // Optional to match the schema (only `source` is required); the backend always
  // emits it. Value set single-sourced from shared/config/tax_treatment.config.json.
  tax_treatment?: TaxTreatment | null;
}

// ── Friendly Reports ─────────────────────────────────────────────────

export type ReportGroupBy = 'month' | 'quarter' | 'year';

export interface FriendlyReportParams {
  start_date: string;
  end_date: string;
  group_by?: ReportGroupBy;
}

export interface InvoiceSummaryPeriod {
  period: string;
  total: number;
  paid: number;
  outstanding: number;
  count: number;
}

export interface InvoiceSummaryCustomer {
  customer_name: string;
  total: number;
  paid: number;
  outstanding: number;
  count: number;
}

export interface InvoiceAging {
  current: number;
  days_1_30: number;
  days_31_60: number;
  days_61_90: number;
  days_90_plus: number;
  total: number;
}

export interface InvoiceSummaryReport {
  start_date: string;
  end_date: string;
  group_by: string;
  summary: {
    total_invoiced: number;
    total_paid: number;
    total_outstanding: number;
    count: number;
    /** Reporting base currency the totals are denominated in (FX-aware). */
    base_currency: string;
    /** Foreign invoices excluded because they carry no usable base-currency snapshot. */
    unconvertible_count: number;
  };
  by_period: InvoiceSummaryPeriod[];
  by_customer: InvoiceSummaryCustomer[];
  aging: InvoiceAging;
}

export interface BillSummaryPeriod {
  period: string;
  total: number;
  paid: number;
  unpaid: number;
  count: number;
}

export interface BillSummaryVendor {
  vendor_name: string;
  total: number;
  count: number;
}

export interface BillSummaryCategory {
  account_id: string;
  account_number: string;
  account_name: string;
  total: number;
  line_count: number;
}

export interface BillSummaryReport {
  start_date: string;
  end_date: string;
  group_by: string;
  summary: {
    total_bills: number;
    total_paid: number;
    total_unpaid: number;
    count: number;
    /** Reporting base currency the totals are denominated in (FX-aware). */
    base_currency: string;
    /** Foreign bills excluded because they carry no usable base-currency snapshot. */
    unconvertible_count: number;
  };
  by_period: BillSummaryPeriod[];
  by_vendor: BillSummaryVendor[];
  by_category: BillSummaryCategory[];
}

export interface ProfitLossPeriod {
  period: string;
  revenue: number;
  expenses: number;
  net_profit: number;
}

export interface ProfitLossReport {
  start_date: string;
  end_date: string;
  group_by: string;
  summary: {
    total_revenue: number;
    total_expenses: number;
    net_profit: number;
    tax_collected: number;
    tax_paid: number;
    /** Reporting base currency the totals are denominated in (FX-aware). */
    base_currency: string;
    /** Foreign invoices/bills excluded because they carry no usable base-currency snapshot. */
    unconvertible_count: number;
  };
  by_period: ProfitLossPeriod[];
  revenue_by_account: BillSummaryCategory[];
  expense_by_account: BillSummaryCategory[];
}

// ── Report Trend (Chart view) ────────────────────────────────────────

export type TrendReportType = 'invoices' | 'bills' | 'profit_loss';
export type TrendDimension = 'none' | 'customer' | 'vendor' | 'category';

export interface TrendSeries {
  /** Stable identity: a synthetic key (invoiced/billed/revenue/expenses/net_profit/__other__)
   *  or a dynamic entity name/id (customer, vendor, account). */
  key: string;
  /** Display label — synthetic keys are translated client-side; dynamic keys use this name. */
  label: string;
  /** One value per period, aligned to `periods`. Amounts in dollars. */
  values: number[];
}

export interface TrendReport {
  report: TrendReportType;
  dimension: TrendDimension;
  group_by: ReportGroupBy;
  start_date: string;
  end_date: string;
  periods: string[];
  /** Bar series — stacked when `stacked` is true. */
  series: TrendSeries[];
  /** Overlay line series (e.g. net profit on the P&L chart). */
  line_series: TrendSeries[];
  stacked: boolean;
}

export interface TrendReportParams {
  report: TrendReportType;
  start_date: string;
  end_date: string;
  group_by?: ReportGroupBy;
  dimension?: TrendDimension;
  top_n?: number;
}

// ── Advanced Reports ────────────────────────────────────────────────

export interface AdvancedReportParams {
  start_date: string;
  end_date: string;
}

// Summarized Income
export interface SummarizedIncomePeriod {
  period: string;
  subtotal: number;
  tax: number;
  total: number;
  invoice_count: number;
}

export interface SummarizedIncomeReport {
  start_date: string;
  end_date: string;
  periods: SummarizedIncomePeriod[];
  totals: {
    subtotal: number;
    tax: number;
    total: number;
    invoice_count: number;
  };
}

// Summarized Expenses
export interface ExpenseCategoryPeriodAmount {
  period: string;
  amount: number;
}

export interface ExpenseCategory {
  account_id: string;
  account_number: string;
  account_name: string;
  period_amounts: ExpenseCategoryPeriodAmount[];
  total: number;
}

export interface SummarizedExpensesReport {
  start_date: string;
  end_date: string;
  periods: string[];
  categories: ExpenseCategory[];
  period_totals: ExpenseCategoryPeriodAmount[];
  grand_total: number;
}

// Income by Customer
export interface IncomeByCustomerRow {
  customer_id: string | null;
  customer_name: string;
  subtotal: number;
  tax: number;
  total: number;
  invoice_count: number;
}

export interface IncomeByCustomerReport {
  start_date: string;
  end_date: string;
  customers: IncomeByCustomerRow[];
  totals: {
    subtotal: number;
    total: number;
    customer_count: number;
  };
}

// Expenses by Vendor
export interface ExpensesByVendorRow {
  vendor_id: string | null;
  vendor_name: string;
  subtotal: number;
  tax: number;
  total: number;
  bill_count: number;
}

export interface ExpensesByVendorReport {
  start_date: string;
  end_date: string;
  vendors: ExpensesByVendorRow[];
  totals: {
    subtotal: number;
    total: number;
    vendor_count: number;
  };
}

// Expenses & Payments by Vendor
export interface ExpensesAndPaymentsByVendorRow {
  vendor_id: string | null;
  vendor_name: string;
  total_expenses: number;
  total_payments: number;
  total_outstanding: number;
  bill_count: number;
  payment_count: number;
}

export interface ExpensesAndPaymentsByVendorReport {
  start_date: string;
  end_date: string;
  vendors: ExpensesAndPaymentsByVendorRow[];
  totals: {
    total_expenses: number;
    total_payments: number;
    total_outstanding: number;
    vendor_count: number;
  };
}

// Collection Report
export interface CollectionReportItem {
  invoice_id: string;
  invoice_number: string;
  customer_id: string | null;
  customer_name: string;
  issue_date: string;
  due_date: string | null;
  total: number;
  amount_paid: number;
  amount_due: number;
  /** amount_due in the reporting base currency; null when the foreign invoice has no usable P13 snapshot (excluded from totals). */
  amount_due_in_base: number | null;
  days_overdue: number;
  kind: InvoiceKind;
  status: string;
  currency_code: string;
}

export interface CollectionReport {
  start_date: string | null;
  end_date: string | null;
  items: CollectionReportItem[];
  aging: InvoiceAging;
  totals: {
    total_due: number;
    invoice_count: number;
    /** Foreign invoices excluded from total_due/aging because they carry no usable base-currency snapshot. */
    unconvertible_count: number;
  };
}

export interface CollectionSummaryCustomer {
  customer_id: string | null;
  customer_name: string;
  total_due: number;
  invoice_count: number;
  oldest_due_date: string | null;
}

export interface CollectionSummaryReport {
  start_date: string | null;
  end_date: string | null;
  customers: CollectionSummaryCustomer[];
  totals: {
    total_due: number;
    customer_count: number;
    invoice_count: number;
    /** Foreign invoices excluded from total_due because they carry no usable base-currency snapshot. */
    unconvertible_count: number;
  };
}

// Comparative P&L
export interface ComparativePLAccountRow {
  account_id: string;
  account_number: string;
  account_name: string;
  amount: number;
}

export interface ComparativePLColumn {
  label: string;
  start_date: string;
  end_date: string;
  revenue: ComparativePLAccountRow[];
  direct_costs: ComparativePLAccountRow[];
  expenses: ComparativePLAccountRow[];
  total_revenue: number;
  total_direct_costs: number;
  total_expenses: number;
  gross_profit: number;
  net_income: number;
}

export interface ComparativePLReport {
  mode: 'year_over_year' | 'month_by_month';
  year?: number;
  columns: ComparativePLColumn[];
}

// VAT Report
export interface VATReportPeriod {
  period: string;
  tax_collected: number;
  taxable_income: number;
  invoice_count: number;
  tax_paid: number;
  taxable_expenses: number;
  bill_count: number;
  net_vat: number;
}

export interface VATReturnRateBucket {
  tax_rate_id: string | null;
  rate_name: string | null;
  rate_percent: number | null;
  tax_treatment: TaxTreatment;
  taxable_base: number;
  tax_amount: number;
}

export interface VATReturnJurisdictionBucket {
  jurisdiction: string | null;
  taxable_base: number;
  tax_amount: number;
}

export interface VATReturnTreatmentBucket {
  taxable_base: number;
  tax_amount: number;
}

export interface VATReturnSide {
  by_rate: VATReturnRateBucket[];
  by_jurisdiction: VATReturnJurisdictionBucket[];
  by_treatment: Record<string, VATReturnTreatmentBucket>;
  total_base: number;
  total_tax: number;
}

export interface VATReport {
  start_date: string;
  end_date: string;
  periods: VATReportPeriod[];
  totals: {
    tax_collected: number;
    tax_paid: number;
    net_vat: number;
  };
  // Optional so an offline-cached response from before this field shipped still
  // type-checks against the rendering guard; the backend always includes it.
  vat_return?: {
    output: VATReturnSide;
    input: VATReturnSide;
    net_vat: number;
  };
}

// Withholding Tax
export interface WithholdingTaxCustomerRow {
  customer_id: string | null;
  customer_name: string;
  total_withholding: number;
  total_payments: number;
  payment_count: number;
}

export interface WithholdingTaxCustomerReport {
  start_date: string;
  end_date: string;
  customers: WithholdingTaxCustomerRow[];
  totals: {
    total_withholding: number;
    total_payments: number;
    customer_count: number;
  };
}

export interface WithholdingTaxVendorRow {
  vendor_id: string | null;
  vendor_name: string;
  total_withholding: number;
  total_payments: number;
  payment_count: number;
}

export interface WithholdingTaxVendorReport {
  start_date: string;
  end_date: string;
  vendors: WithholdingTaxVendorRow[];
  totals: {
    total_withholding: number;
    total_payments: number;
    vendor_count: number;
  };
}

// Expense & Withholding Summary
export interface ExpenseWithholdingSummaryRow {
  vendor_id: string | null;
  vendor_name: string;
  total_expenses: number;
  total_withholding: number;
  bill_count: number;
}

export interface ExpenseWithholdingSummaryReport {
  start_date: string;
  end_date: string;
  vendors: ExpenseWithholdingSummaryRow[];
  totals: {
    total_expenses: number;
    total_withholding: number;
    vendor_count: number;
  };
}

// P&L 6111
export interface PL6111Category {
  code: string;
  name: string;
  type: string;
  amount: number;
  accounts: ComparativePLAccountRow[];
}

export interface PL6111Report {
  year: number;
  categories: PL6111Category[];
  totals: {
    total_revenue: number;
    total_direct_costs: number;
    gross_profit: number;
    total_expenses: number;
    net_income: number;
  };
}

// Cash Flow Statement (indirect method)
export interface CashFlowLineItem {
  label: string;
  amount: number;
}

export interface CashFlowStatementReport {
  start_date: string;
  end_date: string;
  net_income: number;
  operating: {
    starting_with_net_income: number;
    adjustments: CashFlowLineItem[];
    total: number;
  };
  investing: {
    items: CashFlowLineItem[];
    total: number;
  };
  financing: {
    items: CashFlowLineItem[];
    total: number;
  };
  net_change_in_cash: number;
  beginning_cash_balance: number;
  ending_cash_balance: number;
  reconciliation_ok: boolean;
}

// Cash Flow Timeseries (chart data)
export type CashFlowGranularity = 'day' | 'week' | 'month' | 'quarter';

export interface CashFlowBucket {
  start_date: string;
  end_date: string;
  label: string;
  operating: number;
  investing: number;
  financing: number;
  net: number;
  running_cash_balance: number;
}

export interface CashFlowTimeseriesReport {
  start_date: string;
  end_date: string;
  granularity: CashFlowGranularity;
  beginning_cash_balance: number;
  buckets: CashFlowBucket[];
}

// AI Account Suggestion
export interface AccountSuggestion {
  account_id: string | null;
  account_name: string | null;
  confidence: number;
  reasoning: string;
}

// Recurring Templates
export type RecurringFrequency = 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'yearly';

export interface RecurringTemplate {
  id: string;
  tenant_id: string;
  template_type: string;
  name: string;
  frequency: RecurringFrequency;
  next_date: string;
  end_date: string | null;
  template_data: Record<string, unknown>;
  is_active: boolean;
  last_generated_at: string | null;
  times_generated: number;
  last_error: string | null;
  last_error_at: string | null;
  consecutive_failures: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateRecurringFromInvoice {
  invoice_id: string;
  frequency?: RecurringFrequency;
  end_date?: string;
  name?: string;
}

export interface CreateRecurringFromBill {
  bill_id: string;
  frequency?: RecurringFrequency;
  end_date?: string;
  name?: string;
}

export interface UpdateRecurring {
  name?: string;
  frequency?: RecurringFrequency;
  next_date?: string;
  /** null clears the end date (runs indefinitely); omit to leave unchanged. */
  end_date?: string | null;
}

export interface ListRecurringParams {
  template_type?: string;
  is_active?: string;
  offset?: number;
  limit?: number;
}

// ============== Invoice Register (Contract D, T3) ==============

export type RegisterRunType = 'generate' | 'post';

export interface InvoiceRegisterRun {
  id: string;
  tenant_id: string;
  company_id: string;
  run_type: RegisterRunType;
  cutoff_date: string;
  consolidate_per_customer: boolean;
  invoice_count: number;
  total_amount: number;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface RegisterRunWithInvoices extends InvoiceRegisterRun {
  invoices: Invoice[];
}

export interface RegisterGenerateCandidate {
  order_id: string;
  order_number: string;
  customer_id: string | null;
  uninvoiced_amount: number;
  currency: string;
}

export interface RegisterGeneratePreview {
  orders: RegisterGenerateCandidate[];
  order_count: number;
  total_amount: number;
}

export interface RegisterPostCandidate {
  invoice_id: string;
  invoice_number: string;
  customer_name: string | null;
  issue_date: string | null;
  total: number;
  currency: string;
}

export interface RegisterPostPreview {
  invoices: RegisterPostCandidate[];
  invoice_count: number;
  total_amount: number;
}

export interface RunGenerateRequest {
  company_id: string;
  cutoff_date: string;
  consolidate_per_customer?: boolean;
  order_ids?: string[];
  notes?: string;
}

export interface RunPostRequest {
  company_id: string;
  cutoff_date: string;
  notes?: string;
}

// ============== T19 — Bank Deposits / Transfers / Direct Fee Posting ==============

// Single-sourced from the generated BankDeposit/BankTransfer status enums
// (bank_deposit/bank_transfer schemas) — imported above and re-exported here so
// the set never drifts from the backend.
export type { BankDepositStatus, BankTransferStatus };
export type PaymentReconciliationStatus =
  | 'pending_deposit'
  | 'deposited'
  | 'cleared'
  | 'voided';

// BankDeposit, BankDepositLine, and BankTransfer are JSON-schema-first — generated
// from shared/schemas/accounting/{bank_deposit,bank_deposit_line,bank_transfer}.schema.json
// and surfaced to TS via the typed GET/POST bank-deposit / bank-transfer response_models
// ({data: BankDeposit} / {data: BankTransfer} envelopes in bank_deposits_routes.py).
// This keeps the frontend in lockstep with the backend to_dict() shape and puts these
// entities under the check-frontend-api-drift gate. `lines`/`line_count` are optional
// because the deposits list endpoint serializes with include_lines=False. The
// denormalized BankDepositLine display fields (payment_number/contact/method/reference/
// date, gl_account_number/name) are now declared in bank_deposit_line.schema.json, so
// they flow through the generated type — no hand-written mirror needed.
export type { BankDeposit } from '../../../generated/api/models/bankDeposit';
export type { BankDepositLine } from '../../../generated/api/models/bankDepositLine';
export type { BankTransfer } from '../../../generated/api/models/bankTransfer';

export interface BankDepositCreate {
  bank_account_id: string;
  deposit_date: string;
  company_id?: string;
  memo?: string;
}

export interface BankDepositUpdate {
  deposit_date?: string;
  memo?: string;
}

export interface BankDepositReceiptLineCreate {
  line_type: 'receipt';
  payment_id: string;
  memo?: string;
}

export interface BankDepositGlLineCreate {
  line_type: 'gl';
  gl_account_id: string;
  amount: number;
  memo?: string;
}

export type BankDepositLineCreate =
  | BankDepositReceiptLineCreate
  | BankDepositGlLineCreate;

export interface BankDepositValidateResult {
  ok: boolean;
  total: number;
  line_count: number;
  errors: string[];
}

export interface BankDepositPostRequest {
  deposit_slip_total: number;
}

export interface BankDepositVoidRequest {
  reason: string;
}

export interface ListBankDepositsParams {
  status?: BankDepositStatus;
  bank_account_id?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface PendingReceipt {
  id: string;
  payment_number: string;
  payment_date: string;
  contact_id: string | null;
  contact_name: string | null;
  payment_method: string | null;
  amount: number;
  currency_code: string;
  deposit_to_account_id: string;
  deposit_to_account_name: string | null;
  reference: string | null;
  company_id: string;
}

export interface ListPendingReceiptsParams {
  deposit_to_account_id?: string;
  currency_code?: string;
  company_id?: string;
}

export interface BankTransferCreate {
  from_bank_account_id: string;
  to_bank_account_id: string;
  amount: number;
  /**
   * Cross-currency only: the amount actually credited to the destination
   * account, in the to-bank (receiving) currency. Omitted for same-currency
   * transfers and when the tenant's configured rate should be used verbatim.
   */
  to_amount?: number;
  transfer_date: string;
  company_id?: string;
  memo?: string;
  post_immediately?: boolean;
}

export interface BankTransferVoidRequest {
  reason: string;
}

export interface ListBankTransfersParams {
  status?: BankTransferStatus;
  bank_account_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface BankFeePostRequest {
  bank_account_id: string;
  fee_date: string;
  amount: number;
  expense_account_id: string;
  memo?: string;
}

export interface ConfirmDepositMatchRequest {
  transaction_id: string;
  deposit_id: string;
}

export interface DepositRoutingDefault {
  deposit_to_account_id: string | null;
}

// ============== Chart of Accounts tabular import ==============

/** A column found in an uploaded import file. */
export interface CoaImportColumn {
  key: string;
  label: string;
  type: string;
}

/** A target Account field a column can be mapped onto. */
export interface CoaImportTargetField {
  key: string;
  label: string;
  required: boolean;
}

/** Preview returned after parsing an uploaded chart-of-accounts file. */
export interface CoaImportPreview {
  columns: CoaImportColumn[];
  target_fields: CoaImportTargetField[];
  /** { source_column_key: target_field_key } */
  suggested_mappings: Record<string, string>;
  sample_rows: Record<string, unknown>[];
  total_rows: number;
  validation_errors: string[];
}

/** A single skipped/failed row in an import result. */
export interface CoaImportRowError {
  row_index: number;
  status: 'skipped' | 'failed';
  message: string;
}

/** Summary returned after running a chart-of-accounts import. */
export interface CoaImportResult {
  import_id: string;
  total_records: number;
  imported_records: number;
  skipped_records: number;
  failed_records: number;
  row_errors: CoaImportRowError[];
}
